import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

const tabs = [
  { id: "general", label: "General Information", icon: "info" },
  { id: "address", label: "Registered Address", icon: "location_on" },
  { id: "branches", label: "Branch Offices", icon: "business" },
  { id: "bank", label: "Bank Details", icon: "account_balance" },
  { id: "tax", label: "Tax & Compliance", icon: "gavel" },
  { id: "branding", label: "Branding", icon: "palette" },
  { id: "invoice", label: "Invoice Settings", icon: "settings_applications" },
  { id: "payment", label: "Payment Settings", icon: "payments" },
  { id: "email", label: "Email Settings", icon: "mail" },
  { id: "whatsapp", label: "WhatsApp Settings", icon: "chat" },
  { id: "documents", label: "Company Documents", icon: "folder_open" },
  { id: "business", label: "Business Info", icon: "work" },
  { id: "social", label: "Social Media", icon: "share" }
];

export default function CompanyProfile() {
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({});

  // Branch offices state
  const [newBranch, setNewBranch] = useState({
    branchName: "",
    gstNumber: "",
    address: "",
    phone: "",
    email: "",
    contactPerson: ""
  });

  // Bank details state
  const [newBank, setNewBank] = useState({
    accountTypeLabel: "",
    bankName: "",
    accountHolder: "",
    accountNumber: "",
    ifscCode: "",
    branch: "",
    swiftCode: "",
    upiId: "",
    qrCode: "",
    cancelledChequeUpload: ""
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/admin-settings/company-profile", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setProfile(res.data || {});
    } catch (err) {
      console.error(err);
      alert("Failed to load company profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      const res = await axios.put(API_BASE_URL + "/api/admin-settings/company-profile", profile, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setProfile(res.data);
      alert("Company Profile saved successfully!");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setProfile((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value
      }
    }));
  };

  const addBranch = () => {
    if (!newBranch.branchName) return alert("Branch Name is required");
    const currentBranches = Array.isArray(profile.branches) ? profile.branches : [];
    setProfile((prev) => ({
      ...prev,
      branches: [...currentBranches, newBranch]
    }));
    setNewBranch({
      branchName: "",
      gstNumber: "",
      address: "",
      phone: "",
      email: "",
      contactPerson: ""
    });
  };

  const removeBranch = (index) => {
    const currentBranches = Array.isArray(profile.branches) ? profile.branches : [];
    setProfile((prev) => ({
      ...prev,
      branches: currentBranches.filter((_, i) => i !== index)
    }));
  };

  const addBank = () => {
    if (!newBank.accountTypeLabel) return alert("Account Type / Name (e.g. OD, SAVINGS) is required");
    if (!newBank.bankName) return alert("Bank Name is required");
    const currentBanks = Array.isArray(profile.banks) ? profile.banks : [];
    setProfile((prev) => ({
      ...prev,
      banks: [...currentBanks, newBank]
    }));
    setNewBank({
      accountTypeLabel: "",
      bankName: "",
      accountHolder: "",
      accountNumber: "",
      ifscCode: "",
      branch: "",
      swiftCode: "",
      upiId: "",
      qrCode: "",
      cancelledChequeUpload: ""
    });
  };

  const removeBank = (index) => {
    const currentBanks = Array.isArray(profile.banks) ? profile.banks : [];
    setProfile((prev) => ({
      ...prev,
      banks: currentBanks.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-lg font-semibold text-slate-500">Loading Company Profile...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Company Profile</h1>
          <p className="text-sm text-slate-500 mt-1">Configure company profile settings used across documents, invoices and emails.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 font-semibold text-sm shadow-md transition disabled:bg-slate-400"
        >
          <span className="material-symbols-outlined text-sm">save</span>
          {saving ? "Saving..." : "Save Profile"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left side Tab selectors */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-3 shadow-sm h-fit">
          <nav className="space-y-1">
            {tabs.map((t) => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold transition ${
                    active
                      ? "bg-blue-550/10 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-l-4 border-blue-600"
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  }`}
                >
                  <span className="material-symbols-outlined text-lg leading-none">{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right side form rendering */}
        <div className="lg:col-span-3 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <form onSubmit={handleSave} className="space-y-6">
            
            {/* 1. General Information */}
            {activeTab === "general" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">General Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Company Name" value={profile.general?.companyName} onChange={(val) => updateField("general", "companyName", val)} />
                  <FormInput label="Business Name" value={profile.general?.businessName} onChange={(val) => updateField("general", "businessName", val)} />
                  <FormInput label="Proprietor / Director" value={profile.general?.proprietorDirector} onChange={(val) => updateField("general", "proprietorDirector", val)} />
                  <FormInput label="Establishment Year" type="number" value={profile.general?.establishmentYear} onChange={(val) => updateField("general", "establishmentYear", val)} />
                  <FormInput label="GSTIN" value={profile.general?.gstin} onChange={(val) => updateField("general", "gstin", val)} />
                  <FormInput label="PAN" value={profile.general?.pan} onChange={(val) => updateField("general", "pan", val)} />
                  <FormInput label="MSME Registration No" value={profile.general?.msmeRegistrationNumber} onChange={(val) => updateField("general", "msmeRegistrationNumber", val)} />
                  <FormInput label="LUT Number" value={profile.general?.lutNumber} onChange={(val) => updateField("general", "lutNumber", val)} />
                  <FormInput label="IEC Code" value={profile.general?.iecCode} onChange={(val) => updateField("general", "iecCode", val)} />
                  <FormInput label="CIN Number" value={profile.general?.cinNumber} onChange={(val) => updateField("general", "cinNumber", val)} />
                  <FormInput label="TAN Number" value={profile.general?.tanNumber} onChange={(val) => updateField("general", "tanNumber", val)} />
                  <FormInput label="Website" value={profile.general?.website} onChange={(val) => updateField("general", "website", val)} />
                  <FormInput label="Company Email" type="email" value={profile.general?.companyEmail} onChange={(val) => updateField("general", "companyEmail", val)} />
                  <FormInput label="Support Email" type="email" value={profile.general?.supportEmail} onChange={(val) => updateField("general", "supportEmail", val)} />
                  <FormInput label="Phone Number" value={profile.general?.phoneNumber} onChange={(val) => updateField("general", "phoneNumber", val)} />
                  <FormInput label="WhatsApp Number" value={profile.general?.whatsAppNumber} onChange={(val) => updateField("general", "whatsAppNumber", val)} />
                  <FormInput label="Company Logo URL" value={profile.general?.companyLogo} onChange={(val) => updateField("general", "companyLogo", val)} />
                </div>
              </div>
            )}

            {/* 2. Registered Address */}
            {activeTab === "address" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Registered Address</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Door / Shop No." value={profile.address?.doorNo} onChange={(val) => updateField("address", "doorNo", val)} />
                  <FormInput label="Street Name" value={profile.address?.street} onChange={(val) => updateField("address", "street", val)} />
                  <FormInput label="Area / Locality" value={profile.address?.area} onChange={(val) => updateField("address", "area", val)} />
                  <FormInput label="City" value={profile.address?.city} onChange={(val) => updateField("address", "city", val)} />
                  <FormInput label="District" value={profile.address?.district} onChange={(val) => updateField("address", "district", val)} />
                  <FormInput label="State" value={profile.address?.state} onChange={(val) => updateField("address", "state", val)} />
                  <FormInput label="Country" value={profile.address?.country} onChange={(val) => updateField("address", "country", val)} />
                  <FormInput label="PIN Code" value={profile.address?.pinCode} onChange={(val) => updateField("address", "pinCode", val)} />
                </div>
              </div>
            )}

            {/* 3. Branch Offices */}
            {activeTab === "branches" && (
              <div className="space-y-6">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Branch Offices</h2>
                
                {/* Branch adder */}
                <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-4">
                  <h3 className="font-bold text-sm text-slate-700 dark:text-slate-300">Add New Branch</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FormInput label="Branch Name" value={newBranch.branchName} onChange={(val) => setNewBranch(p => ({ ...p, branchName: val }))} />
                    <FormInput label="GST Number" value={newBranch.gstNumber} onChange={(val) => setNewBranch(p => ({ ...p, gstNumber: val }))} />
                    <FormInput label="Phone" value={newBranch.phone} onChange={(val) => setNewBranch(p => ({ ...p, phone: val }))} />
                    <FormInput label="Email" type="email" value={newBranch.email} onChange={(val) => setNewBranch(p => ({ ...p, email: val }))} />
                    <FormInput label="Contact Person" value={newBranch.contactPerson} onChange={(val) => setNewBranch(p => ({ ...p, contactPerson: val }))} />
                    <FormInput label="Address" value={newBranch.address} onChange={(val) => setNewBranch(p => ({ ...p, address: val }))} />
                  </div>
                  <button
                    type="button"
                    onClick={addBranch}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow transition"
                  >
                    + Add Branch to List
                  </button>
                </div>

                {/* Branches List */}
                <div className="space-y-3">
                  <h3 className="font-bold text-sm text-slate-700 dark:text-slate-300">Configured Branches</h3>
                  {(!profile.branches || profile.branches.length === 0) ? (
                    <p className="text-sm text-slate-500 italic">No branch offices configured yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {profile.branches.map((b, idx) => (
                        <div key={idx} className="border border-slate-300 dark:border-slate-700 rounded-xl p-4 relative bg-slate-50/50 dark:bg-slate-800">
                          <button
                            type="button"
                            onClick={() => removeBranch(idx)}
                            className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                          >
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                          <h4 className="font-bold text-slate-800 dark:text-white text-base">{b.branchName}</h4>
                          <div className="mt-2 text-xs space-y-1 text-slate-600 dark:text-slate-400">
                            <p><strong>GSTIN:</strong> {b.gstNumber || "-"}</p>
                            <p><strong>Phone:</strong> {b.phone || "-"}</p>
                            <p><strong>Email:</strong> {b.email || "-"}</p>
                            <p><strong>Contact Person:</strong> {b.contactPerson || "-"}</p>
                            <p><strong>Address:</strong> {b.address || "-"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 4. Bank Details */}
            {activeTab === "bank" && (
              <div className="space-y-6">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Bank Details</h2>
                
                {/* Bank adder */}
                <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-4">
                  <h3 className="font-bold text-sm text-slate-700 dark:text-slate-300">Add New Bank Account</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FormInput label="Account Type / Label (e.g. OD Account, Current Account)" value={newBank.accountTypeLabel} onChange={(val) => setNewBank(p => ({ ...p, accountTypeLabel: val }))} />
                    <FormInput label="Bank Name" value={newBank.bankName} onChange={(val) => setNewBank(p => ({ ...p, bankName: val }))} />
                    <FormInput label="Account Holder Name" value={newBank.accountHolder} onChange={(val) => setNewBank(p => ({ ...p, accountHolder: val }))} />
                    <FormInput label="Account Number" value={newBank.accountNumber} onChange={(val) => setNewBank(p => ({ ...p, accountNumber: val }))} />
                    <FormInput label="IFSC Code" value={newBank.ifscCode} onChange={(val) => setNewBank(p => ({ ...p, ifscCode: val }))} />
                    <FormInput label="Branch Office" value={newBank.branch} onChange={(val) => setNewBank(p => ({ ...p, branch: val }))} />
                    <FormInput label="SWIFT Code" value={newBank.swiftCode} onChange={(val) => setNewBank(p => ({ ...p, swiftCode: val }))} />
                    <FormInput label="UPI ID" value={newBank.upiId} onChange={(val) => setNewBank(p => ({ ...p, upiId: val }))} />
                    <FormInput label="QR Code Image Link" value={newBank.qrCode} onChange={(val) => setNewBank(p => ({ ...p, qrCode: val }))} />
                    <FormInput label="Cancelled Cheque Image Link" value={newBank.cancelledChequeUpload} onChange={(val) => setNewBank(p => ({ ...p, cancelledChequeUpload: val }))} />
                  </div>
                  <button
                    type="button"
                    onClick={addBank}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow transition"
                  >
                    + Add Bank Account to List
                  </button>
                </div>

                {/* Banks List */}
                <div className="space-y-3">
                  <h3 className="font-bold text-sm text-slate-700 dark:text-slate-300">Configured Bank Accounts</h3>
                  {(!profile.banks || profile.banks.length === 0) ? (
                    <p className="text-sm text-slate-500 italic">No bank accounts configured yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {profile.banks.map((b, idx) => (
                        <div key={idx} className="border border-slate-300 dark:border-slate-700 rounded-xl p-4 relative bg-slate-50/50 dark:bg-slate-800">
                          <button
                            type="button"
                            onClick={() => removeBank(idx)}
                            className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                          >
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                          <h4 className="font-bold text-blue-600 dark:text-blue-400 text-base flex items-center gap-2">
                            <span className="material-symbols-outlined text-lg">account_balance</span>
                            {b.accountTypeLabel}
                          </h4>
                          <div className="mt-2 text-xs space-y-1 text-slate-600 dark:text-slate-400">
                            <p><strong>Bank Name:</strong> {b.bankName || "-"}</p>
                            <p><strong>Holder Name:</strong> {b.accountHolder || "-"}</p>
                            <p><strong>A/c No:</strong> {b.accountNumber || "-"}</p>
                            <p><strong>IFS Code:</strong> {b.ifscCode || "-"}</p>
                            <p><strong>Branch:</strong> {b.branch || "-"}</p>
                            <p><strong>SWIFT:</strong> {b.swiftCode || "-"}</p>
                            <p><strong>UPI ID:</strong> {b.upiId || "-"}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 5. Tax & Compliance */}
            {activeTab === "tax" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Tax & Compliance</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="GST Number" value={profile.taxCompliance?.gstNumber} onChange={(val) => updateField("taxCompliance", "gstNumber", val)} />
                  <FormInput label="PAN Number" value={profile.taxCompliance?.panNumber} onChange={(val) => updateField("taxCompliance", "panNumber", val)} />
                  <FormInput label="MSME Number" value={profile.taxCompliance?.msmeNumber} onChange={(val) => updateField("taxCompliance", "msmeNumber", val)} />
                  <FormInput label="LUT Number" value={profile.taxCompliance?.lutNumber} onChange={(val) => updateField("taxCompliance", "lutNumber", val)} />
                  <FormInput label="IEC Code" value={profile.taxCompliance?.iecCode} onChange={(val) => updateField("taxCompliance", "iecCode", val)} />
                  <FormInput label="TAN Number" value={profile.taxCompliance?.tanNumber} onChange={(val) => updateField("taxCompliance", "tanNumber", val)} />
                  <FormInput label="Professional Tax" value={profile.taxCompliance?.professionalTax} onChange={(val) => updateField("taxCompliance", "professionalTax", val)} />
                  <FormInput label="PF Number" value={profile.taxCompliance?.pfNumber} onChange={(val) => updateField("taxCompliance", "pfNumber", val)} />
                  <FormInput label="ESI Number" value={profile.taxCompliance?.esiNumber} onChange={(val) => updateField("taxCompliance", "esiNumber", val)} />
                  <FormInput label="Factory License" value={profile.taxCompliance?.factoryLicense} onChange={(val) => updateField("taxCompliance", "factoryLicense", val)} />
                  <FormInput label="Electrical Contractor License" value={profile.taxCompliance?.electricalContractorLicense} onChange={(val) => updateField("taxCompliance", "electricalContractorLicense", val)} />
                  <FormInput label="ISO Certificate Number" value={profile.taxCompliance?.isoCertificateNumber} onChange={(val) => updateField("taxCompliance", "isoCertificateNumber", val)} />
                  <FormInput label="Digital Signature Certificate" value={profile.taxCompliance?.digitalSignatureCertificate} onChange={(val) => updateField("taxCompliance", "digitalSignatureCertificate", val)} />
                </div>
              </div>
            )}

            {/* 6. Branding */}
            {activeTab === "branding" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Branding</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Company Logo Link" value={profile.branding?.companyLogo} onChange={(val) => updateField("branding", "companyLogo", val)} />
                  <FormInput label="Letterhead Layout link" value={profile.branding?.letterhead} onChange={(val) => updateField("branding", "letterhead", val)} />
                  <FormInput label="Company Seal Stamp Link" value={profile.branding?.companySeal} onChange={(val) => updateField("branding", "companySeal", val)} />
                  <FormInput label="Authorized Signature Link" value={profile.branding?.authorizedSignature} onChange={(val) => updateField("branding", "authorizedSignature", val)} />
                  <FormInput label="Footer Text" value={profile.branding?.footerText} onChange={(val) => updateField("branding", "footerText", val)} />
                  <FormInput label="Watermark Link" value={profile.branding?.watermark} onChange={(val) => updateField("branding", "watermark", val)} />
                </div>
              </div>
            )}

            {/* 7. Invoice & Document Settings */}
            {activeTab === "invoice" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Invoice &amp; Document Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Invoice Prefix" value={profile.documentSettings?.invoicePrefix} onChange={(val) => updateField("documentSettings", "invoicePrefix", val)} />
                  <FormInput label="Quotation Prefix" value={profile.documentSettings?.quotationPrefix} onChange={(val) => updateField("documentSettings", "quotationPrefix", val)} />
                  <FormInput label="Purchase Order Prefix" value={profile.documentSettings?.purchaseOrderPrefix} onChange={(val) => updateField("documentSettings", "purchaseOrderPrefix", val)} />
                  <FormInput label="Purchase Invoice Prefix" value={profile.documentSettings?.purchaseInvoicePrefix} onChange={(val) => updateField("documentSettings", "purchaseInvoicePrefix", val)} />
                  <FormInput label="Service Report Prefix" value={profile.documentSettings?.serviceReportPrefix} onChange={(val) => updateField("documentSettings", "serviceReportPrefix", val)} />
                  <FormInput label="Complaint Prefix" value={profile.documentSettings?.complaintPrefix} onChange={(val) => updateField("documentSettings", "complaintPrefix", val)} />
                  <FormInput label="Customer Prefix" value={profile.documentSettings?.customerPrefix} onChange={(val) => updateField("documentSettings", "customerPrefix", val)} />
                  <FormInput label="Supplier Prefix" value={profile.documentSettings?.supplierPrefix} onChange={(val) => updateField("documentSettings", "supplierPrefix", val)} />
                  <FormInput label="Engineer Prefix" value={profile.documentSettings?.engineerPrefix} onChange={(val) => updateField("documentSettings", "engineerPrefix", val)} />
                  <FormInput label="Accounts Prefix" value={profile.documentSettings?.accountsPrefix} onChange={(val) => updateField("documentSettings", "accountsPrefix", val)} />
                </div>
              </div>
            )}

            {/* 8. Payment Settings */}
            {activeTab === "payment" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Payment Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Default Payment Terms" value={profile.paymentSettings?.defaultPaymentTerms} onChange={(val) => updateField("paymentSettings", "defaultPaymentTerms", val)} />
                  <FormInput label="Credit Days" type="number" value={profile.paymentSettings?.creditDays} onChange={(val) => updateField("paymentSettings", "creditDays", val)} />
                  <FormInput label="Bank Charges (%)" type="number" value={profile.paymentSettings?.bankCharges} onChange={(val) => updateField("paymentSettings", "bankCharges", val)} />
                  <FormInput label="Base Currency" value={profile.paymentSettings?.currency} onChange={(val) => updateField("paymentSettings", "currency", val)} />
                  <FormInput label="Tax Type" value={profile.paymentSettings?.taxType} onChange={(val) => updateField("paymentSettings", "taxType", val)} />
                  <FormInput label="Round Off Settings" value={profile.paymentSettings?.roundOffSettings} onChange={(val) => updateField("paymentSettings", "roundOffSettings", val)} />
                </div>
              </div>
            )}

            {/* 9. Email Settings */}
            {activeTab === "email" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Email Configuration (SMTP)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="SMTP Server Address" value={profile.emailSettings?.smtpServer} onChange={(val) => updateField("emailSettings", "smtpServer", val)} />
                  <FormInput label="SMTP Port" type="number" value={profile.emailSettings?.port} onChange={(val) => updateField("emailSettings", "port", val)} />
                  <FormInput label="Username" value={profile.emailSettings?.username} onChange={(val) => updateField("emailSettings", "username", val)} />
                  <FormInput label="Password" type="password" value={profile.emailSettings?.password} onChange={(val) => updateField("emailSettings", "password", val)} />
                  <FormInput label="From Email Address" type="email" value={profile.emailSettings?.fromEmail} onChange={(val) => updateField("emailSettings", "fromEmail", val)} />
                  <FormInput label="Reply-To Email Address" type="email" value={profile.emailSettings?.replyToEmail} onChange={(val) => updateField("emailSettings", "replyToEmail", val)} />
                </div>
              </div>
            )}

            {/* 10. WhatsApp Settings */}
            {activeTab === "whatsapp" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">WhatsApp Configuration (API)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="API Token" value={profile.whatsAppSettings?.apiToken} onChange={(val) => updateField("whatsAppSettings", "apiToken", val)} />
                  <FormInput label="WhatsApp Business Number" value={profile.whatsAppSettings?.businessNumber} onChange={(val) => updateField("whatsAppSettings", "businessNumber", val)} />
                  <FormInput label="Default Message Template" value={profile.whatsAppSettings?.messageTemplate} onChange={(val) => updateField("whatsAppSettings", "messageTemplate", val)} />
                  
                  <div className="md:col-span-2 flex flex-col gap-3 mt-2 bg-slate-50 dark:bg-slate-700/20 border p-4 rounded-xl">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!profile.whatsAppSettings?.autoSendReports}
                        onChange={(e) => updateField("whatsAppSettings", "autoSendReports", e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Auto Send Service Reports on closure</span>
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!profile.whatsAppSettings?.autoSendQuotations}
                        onChange={(e) => updateField("whatsAppSettings", "autoSendQuotations", e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Auto Send Quotations on approval</span>
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!profile.whatsAppSettings?.autoSendInvoice}
                        onChange={(e) => updateField("whatsAppSettings", "autoSendInvoice", e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Auto Send Invoices on generation</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* 11. Company Documents */}
            {activeTab === "documents" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Company Documents</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="GST Registration Certificate Link" value={profile.companyDocuments?.gstCertificate} onChange={(val) => updateField("companyDocuments", "gstCertificate", val)} />
                  <FormInput label="MSME Certificate Link" value={profile.companyDocuments?.msmeCertificate} onChange={(val) => updateField("companyDocuments", "msmeCertificate", val)} />
                  <FormInput label="PAN Card Link" value={profile.companyDocuments?.panCard} onChange={(val) => updateField("companyDocuments", "panCard", val)} />
                  <FormInput label="Cancelled Cheque Link" value={profile.companyDocuments?.cancelledCheque} onChange={(val) => updateField("companyDocuments", "cancelledCheque", val)} />
                  <FormInput label="ISO Certificate Link" value={profile.companyDocuments?.isoCertificate} onChange={(val) => updateField("companyDocuments", "isoCertificate", val)} />
                  <FormInput label="Company Registration Docs Link" value={profile.companyDocuments?.companyRegistration} onChange={(val) => updateField("companyDocuments", "companyRegistration", val)} />
                  <FormInput label="Electrical License Link" value={profile.companyDocuments?.electricalLicense} onChange={(val) => updateField("companyDocuments", "electricalLicense", val)} />
                  <FormInput label="Vendor Certificates Link" value={profile.companyDocuments?.vendorCertificates} onChange={(val) => updateField("companyDocuments", "vendorCertificates", val)} />
                  <FormInput label="Factory License Link" value={profile.companyDocuments?.factoryLicense} onChange={(val) => updateField("companyDocuments", "factoryLicense", val)} />
                </div>
              </div>
            )}

            {/* 12. Business Information */}
            {activeTab === "business" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Business Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="Nature of Business" value={profile.businessInfo?.natureOfBusiness} onChange={(val) => updateField("businessInfo", "natureOfBusiness", val)} />
                  <FormInput label="Key Products (Comma separated)" value={profile.businessInfo?.products} onChange={(val) => updateField("businessInfo", "products", val)} />
                  <FormInput label="Key Services Offered" value={profile.businessInfo?.services} onChange={(val) => updateField("businessInfo", "services", val)} />
                  <FormInput label="Industries Served" value={profile.businessInfo?.industriesServed} onChange={(val) => updateField("businessInfo", "industriesServed", val)} />
                  <FormInput label="Annual Turnover" value={profile.businessInfo?.annualTurnover} onChange={(val) => updateField("businessInfo", "annualTurnover", val)} />
                  <FormInput label="Number of Employees" type="number" value={profile.businessInfo?.numberEmployees} onChange={(val) => updateField("businessInfo", "numberEmployees", val)} />
                  <FormInput label="Working Hours" value={profile.businessInfo?.workingHours} onChange={(val) => updateField("businessInfo", "workingHours", val)} />
                </div>
              </div>
            )}

            {/* 13. Social Media */}
            {activeTab === "social" && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white border-b pb-2">Social Media Channels</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput label="LinkedIn URL" value={profile.socialMedia?.linkedIn} onChange={(val) => updateField("socialMedia", "linkedIn", val)} />
                  <FormInput label="Facebook Page URL" value={profile.socialMedia?.facebook} onChange={(val) => updateField("socialMedia", "facebook", val)} />
                  <FormInput label="Instagram URL" value={profile.socialMedia?.instagram} onChange={(val) => updateField("socialMedia", "instagram", val)} />
                  <FormInput label="YouTube Channel URL" value={profile.socialMedia?.youtube} onChange={(val) => updateField("socialMedia", "youtube", val)} />
                  <FormInput label="Twitter (X) URL" value={profile.socialMedia?.twitter} onChange={(val) => updateField("socialMedia", "twitter", val)} />
                  <FormInput label="Google Business Map Link" value={profile.socialMedia?.googleBusiness} onChange={(val) => updateField("socialMedia", "googleBusiness", val)} />
                </div>
              </div>
            )}

          </form>
        </div>
      </div>
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text" }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">{label}</label>
      <input
        type={type}
        value={value === null || value === undefined ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 text-slate-900 dark:text-white"
      />
    </div>
  );
}
